import { Application } from "@core/Application";
import { Analytics } from "./Analytics";
import { trackAnalyticsSafely } from "./AnalyticsTracker";

type QontactPointEditAnalyticsPayload = {
    qontactPointID: string,
    buildingID: string,
    functionalUnitID: string
}

type QontactPointEditSaveAnalyticsPayload = QontactPointEditAnalyticsPayload & {
    changedName: boolean,
    changedCoordinates: boolean
}

export class AnalyticsHelper {
    private static getAnalytics(): Analytics {
        return Application.shared().coreDependencies.analytics
    }
    
    static trackMainScreenShown() {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.MainScreen.Shown"
        })
    }

    static trackQontactPointDetailShown(
        qontactPointID: string,
        buildingID: string,
        functionalUnitID: string
    ) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.QontactPoint.DetailShown",
            payload: {
                qontactPointID,
                buildingID,
                functionalUnitID,
            }
        })
    }

    static trackQontactPointQRDownloaded(
        qontactPointID: string,
        buildingID: string,
        functionalUnitID: string,
        qrType: 'whatsapp' | 'app'
    ) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.QontactPoint.QRDownloaded",
            payload: {
                qontactPointID,
                buildingID,
                functionalUnitID,
                qrType,
            }
        })
    }

    static trackQontactPointEditShown(payload: QontactPointEditAnalyticsPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.QontactPoint.EditShown",
            payload
        })
    }

    static trackQontactPointEditSaveSubmitted(payload: QontactPointEditSaveAnalyticsPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.QontactPoint.EditSaveSubmitted",
            payload
        })
    }

    static trackQontactPointEditSaveSucceeded(payload: QontactPointEditSaveAnalyticsPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.QontactPoint.EditSaveSucceeded",
            payload
        })
    }

    static trackQontactPointEditSaveFailed(payload: QontactPointEditSaveAnalyticsPayload & { errorMessage?: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.QontactPoint.EditSaveFailed",
            payload
        })
    }

    static trackQontactPointEditCoordinatesHelpOpened(payload: QontactPointEditAnalyticsPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.QontactPoint.EditCoordinatesHelpOpened",
            payload
        })
    }

    static trackPresenteeismFailuresShown(day: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.PresenteeismFailures.Shown",
            payload: {
                day
            }
        })
    }

    static trackPresenteeismFailuresDateChanged(day: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.PresenteeismFailures.DateChanged",
            payload: {
                day
            }
        })
    }

    static trackPresenteeismFailureDetailShown(
        firestoreRecordId: string | undefined,
        ticketID: string | undefined,
        type: string,
        failureReason: string,
        day: string
    ) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.PresenteeismFailures.DetailShown",
            payload: {
                firestoreRecordId,
                ticketID,
                type,
                failureReason,
                day
            }
        })
    }

    static trackAlertsShown(day: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Alerts.Shown",
            payload: {
                day
            }
        })
    }

    static trackAlertsDateChanged(day: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Alerts.DateChanged",
            payload: {
                day
            }
        })
    }

    static trackAlertsRefreshed(day: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Alerts.Refreshed",
            payload: {
                day
            }
        })
    }

    static trackAlertDetailShown(payload: {
        alertID?: string,
        day?: string,
        buildingID?: string,
        buildingName?: string,
        shiftInstanceID?: string,
        recipeID?: string,
        alertType?: string,
        alertImportance?: string
    }) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Alerts.DetailShown",
            payload
        })
    }

    static trackPresenteeismConciliationDetailShown(
        requesterID: string,
        tableRecordKey: string,
        day: string,
        buildingID?: string,
        checkInTimestamp?: number
    ) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.ConciliationDetailShown",
            payload: {
                requesterID,
                tableRecordKey,
                day,
                buildingID,
                checkInTimestamp,
            }
        })
    }

    static trackPresenteeismConciliationCanceled(payload: {
        organizationID?: string,
        requesterID?: string,
        tableRecordKey?: string,
        day?: string,
        buildingID?: string,
        checkinRecordID?: string,
        checkoutRecordID?: string,
        checkInTimestamp?: number,
        reasonLength?: number
    }) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.ConciliationCanceled",
            payload
        })
    }

    static trackPresenteeismConciliationUndo(payload: {
        organizationID?: string,
        requesterID?: string,
        tableRecordKey?: string,
        day?: string,
        buildingID?: string,
        checkinRecordID?: string,
        checkoutRecordID?: string,
        checkInTimestamp?: number,
        cancellationUndoID?: string
    }) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.ConciliationUndo",
            payload
        })
    }

    static trackFichadasSingleDate(date: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.Shown",
            payload: {
                dateFrom: date
            }
        })
    }

    static trackFichadasRange(dateFrom: string, dateTo: string) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.Shown",
            payload: {
                dateFrom: dateFrom,
                dateTo: dateTo
            }
        })
    }

    static trackFichadasRangeExport(
        dateFrom: string, dateTo: string, hasUserFilter: boolean
    ) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.ExcelExported",
            payload: {
                isRange: true,
                hasUserFilter: hasUserFilter,
                dateFrom: dateFrom,
                dateTo: dateTo
            }
        })
    }

    static trackFichadasSingleDateExport(dateFrom: string, hasUserFilter: boolean) {
        let analytics = AnalyticsHelper.getAnalytics()

        analytics.track({
            eventType: "WebDashboard.Fichadas.ExcelExported",
            payload: {
                isRange: false,
                hasUserFilter: hasUserFilter,
                dateFrom: dateFrom,
            }
        })
    }
}
